import * as React from "react";
import BlogPostTemplate from "@/components/blog/BlogPostTemplate";
import { LocalizedLink } from "@/components/LocalizedLink";

const SmartToiletSpecSheetGuide = () => {
  return (
    <BlogPostTemplate
      title="How to Write a Smart Toilet Spec Sheet for Your Next Project"
      metaTitle="How to Write a Smart Toilet Spec Sheet — Architect's Guide"
      metaDescription="An architect's guide to specifying smart toilets: dimensions, power, water pressure, rough-in and clearances, with real spec figures."
      keywords="smart toilet spec sheet, architect smart toilet specification, REM7 dimensions, smart toilet rough-in, bathroom fixture schedule Europe"
      author="Aureum7 Trading"
      date="August 18, 2026"
      readTime="10 min read"
      category="B2B & Projects"
      tags={["Architects", "Specification", "Technical", "B2B"]}
      featuredImage="/lovable-uploads/7a774286-73c5-43d4-b368-b47766cb1465.png"
      featuredImageAlt="Architect reviewing a smart toilet specification drawing"
      relatedProducts={["rem7", "aria", "luxa"]}
      quickAnswer={
        <>
          A usable smart toilet specification lists six things per fixture: model and
          variant, overall dimensions, rough-in type and dimension, water pressure band,
          electrical supply and maximum power draw, and ingress protection. For REM7 those
          are 715 × 625 × 465 mm, 35 kg, S-trap 300±400 mm (customisable 200–450 mm) or
          P-trap ~100 mm, 0.07–0.75 MPa, 220–240V on a dedicated GFCI circuit at 1200W max,
          and IPX4. Everything site-dependent — riser capacity, floor build-up, existing
          waste falls — belongs in a survey note, not a guessed number.
        </>
      }
      faqs={[
        {
          question: "What are REM7's specification dimensions?",
          answer:
            "715 × 625 × 465 mm, weight 35 kg. The published clearance requirements are a minimum 715 mm width, 625 mm depth from wall centre and 465 mm height, plus additional space for the seat and lid to open.",
        },
        {
          question: "What rough-in should the drawing show?",
          answer:
            "REM7 supports an S-trap rough-in of 300±400 mm, customisable between 200 and 450 mm, or a P-trap at approximately 100 mm. Confirm the configuration per bathroom type before the sanitaryware schedule is issued, since it drives the setting-out dimension.",
        },
        {
          question: "What water pressure should be specified?",
          answer:
            "REM7 operates between 0.07 and 0.75 MPa; the wall-hung ARIA operates from 0.05 MPa. Specify the band, and add a note requiring pressure verification at the worst-case outlet on the project rather than assuming the incoming main figure.",
        },
        {
          question: "What electrical information belongs on the sheet?",
          answer:
            "Supply of 220–240V AC on a dedicated GFCI-protected outlet, 1200W maximum draw for REM7, IPX4 ingress protection, professional electrical installation, and outlet position outside the wet zone per local regulation. ARIA is listed at 110–240V, 50/60Hz.",
        },
        {
          question: "What should not be written as a fixed number?",
          answer:
            "Riser capacity, achievable pressure at the top floor, floor build-up, existing waste fall, cavity depth for a carrier frame and grab-rail interaction are all site conditions. Specify them as survey items with a required outcome rather than assuming a dimension.",
        },
        {
          question: "How do I get datasheets and drawings?",
          answer:
            "The REM7 specifications document is downloadable from the REM7 product page, and our projects team supplies further documentation for tender packages on request at info@aureum7trading.com.",
        },
      ]}
    >
      <div className="space-y-6">
        <p className="text-lg">
          Most smart toilet specification problems on site trace back to a spec sheet that
          named a product but not its requirements. This is the shortest set of fields that
          reliably prevents that — with the published figures for our reference model,{" "}
          <LocalizedLink to="/products/rem7" className="text-primary underline underline-offset-4">
            REM7
          </LocalizedLink>
          , filled in as a worked example.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">1. Identification</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Model:</strong> REM7 (flagship series).</li>
          <li><strong>Variants:</strong> M3 · S1 · K2 · U1 — different bathroom footprints within the same family.</li>
          <li><strong>Installation type:</strong> floor-standing tankless (REM7) or wall-hung (ARIA).</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-8 mb-4">2. Dimensions and Clearances</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>REM7:</strong> 715 × 625 × 465 mm, 35 kg.</li>
          <li><strong>Clearances:</strong> minimum 715 mm width, 625 mm depth from wall centre, 465 mm height, plus space for lid travel.</li>
          <li>
            <strong>
              <LocalizedLink to="/products/aria" className="text-primary underline underline-offset-4">
                ARIA
              </LocalizedLink>{" "}
              (wall-hung):
            </strong>{" "}
            360 × 540 × 340 mm, 32 kg, elongated bowl.
          </li>
          <li>
            <strong>
              <LocalizedLink to="/products/luxa" className="text-primary underline underline-offset-4">
                LUXA
              </LocalizedLink>
              :
            </strong>{" "}
            420 × 360 × 520 mm, 32 kg.
          </li>
        </ul>
        <p>
          Wall-hung fixtures additionally need a carrier frame and sufficient cavity depth.
          That depth is a project condition — record it as a coordination item with the
          partition build-up rather than assuming a standard.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">3. Plumbing</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Rough-in:</strong> S-trap 300±400 mm (customisable 200–450 mm) or P-trap ~100 mm.</li>
          <li><strong>Water pressure:</strong> 0.07–0.75 MPa (REM7); from 0.05 MPa (ARIA); 0.08–0.75 MPa (LUXA).</li>
          <li><strong>Supply:</strong> cold water line with an accessible shut-off valve for maintenance.</li>
          <li><strong>Flush volume (ARIA):</strong> 3/4.5 L dual flush.</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-8 mb-4">4. Electrical</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Supply:</strong> 220–240V AC (REM7); 110–240V, 50/60Hz (ARIA).</li>
          <li><strong>Circuit:</strong> dedicated GFCI-protected outlet, positioned outside the wet zone per local regulation.</li>
          <li><strong>Maximum draw:</strong> 1200W (REM7); 1400W (LUXA).</li>
          <li><strong>Ingress protection:</strong> IPX4.</li>
          <li><strong>Installation:</strong> professional electrical installation, recorded for warranty purposes.</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-8 mb-4">5. Materials, Controls and Compliance</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Antimicrobial composite seat surface (REM7); antibacterial PP resin (ARIA).</li>
          <li>Control method: digital interface with remote; note the wall-panel mounting height where one is specified.</li>
          <li>CE-marked product; file documentation with the project O&amp;M manual.</li>
          <li>Warranty term per model — record it on the schedule rather than in correspondence.</li>
        </ul>

        <h2 className="text-2xl font-semibold mt-8 mb-4">6. What to Leave as a Survey Item</h2>
        <p>
          These are the fields where inventing a figure causes the problem the spec sheet
          was meant to prevent:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Achievable pressure at the highest and furthest outlet on each riser.</li>
          <li>Distribution-board and riser capacity against total connected load.</li>
          <li>Existing waste position and fall in refurbishment work.</li>
          <li>Floor build-up and finished floor level affecting setting-out.</li>
          <li>Cavity depth available for a carrier frame.</li>
          <li>Accessibility clearances and grab-rail positions under the applicable national code.</li>
        </ul>
        <p>
          Write each of these as a required outcome with a responsible party and a date, and
          the sanitaryware schedule stops being the item that holds up first fix.
        </p>

        <h2 className="text-2xl font-semibold mt-8 mb-4">Related Reading</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <LocalizedLink to="/blog/specifying-smart-toilets-multi-unit-residential" className="text-primary underline underline-offset-4">
              Specifying Smart Toilets for Multi-Unit Residential Developments
            </LocalizedLink>
          </li>
          <li>
            <LocalizedLink to="/blog/smart-toilet-roi-boutique-hotels" className="text-primary underline underline-offset-4">
              Smart Toilet ROI for Boutique Hotels: A Developer's Guide
            </LocalizedLink>
          </li>
          <li>
            <LocalizedLink to="/blog/smart-toilet-installation-hotels" className="text-primary underline underline-offset-4">
              Smart Toilet Installation for Hotels
            </LocalizedLink>
          </li>
        </ul>

        <div className="bg-muted p-6 rounded-lg mt-8">
          <h3 className="text-xl font-semibold mb-3">Need documentation for a tender package?</h3>
          <p className="mb-4">
            Our{" "}
            <LocalizedLink to="/business" className="text-primary underline underline-offset-4">
              B2B &amp; projects team
            </LocalizedLink>{" "}
            supplies datasheets and specification support for architects.
            Trade enquiries: info@aureum7trading.com · WhatsApp +34 642 278 939.
          </p>
        </div>
      </div>
    </BlogPostTemplate>
  );
};

export default SmartToiletSpecSheetGuide;
